import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Paper,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Close as CloseIcon,
  Pix as PixIcon,
  Receipt as BoletoIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { usePayment } from '../../contexts/PaymentContext';

/**
 * Modal de pagamento com opcoes de PIX e Boleto
 */
const PaymentModal = () => {
  const {
    modalOpen,
    closePaymentModal,
    selectedCobranca,
    fetchPixData,
    fetchBoletoData,
    loadPaymentStatus
  } = usePayment();

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pixData, setPixData] = useState(null);
  const [boletoData, setBoletoData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Limpar dados ao abrir/fechar modal
  useEffect(() => {
    if (modalOpen) {
      setPixData(null);
      setBoletoData(null);
      setError(null);
      setActiveTab(0);
    }
  }, [modalOpen]);

  // Carregar dados quando tab muda
  useEffect(() => {
    if (!modalOpen || !selectedCobranca) {
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Tentar obter idcobranca da cobranca selecionada ou usar faturaAsaasId
        const idCobranca = selectedCobranca.idcobranca;

        if (!idCobranca) {
          setError('Nenhuma cobranca pendente encontrada');
          setLoading(false);
          return;
        }

        if (activeTab === 0) {
          // PIX
          const data = await fetchPixData(idCobranca);
          setPixData(data);
        } else {
          // Boleto
          const data = await fetchBoletoData(idCobranca);
          setBoletoData(data);
        }
      } catch (err) {
        console.error('Erro ao carregar dados de pagamento:', err);
        setError(err.response?.data?.erro || 'Erro ao carregar dados de pagamento');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [modalOpen, selectedCobranca, activeTab, fetchPixData, fetchBoletoData]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
  };

  const handleCopyPix = async () => {
    if (pixData?.payload) {
      try {
        await navigator.clipboard.writeText(pixData.payload);
        setCopied(true);
        setSnackbarOpen(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  };

  const handleCopyBoleto = async () => {
    if (boletoData?.identificationField) {
      try {
        await navigator.clipboard.writeText(boletoData.identificationField);
        setSnackbarOpen(true);
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  };

  const handleOpenBoleto = () => {
    if (boletoData?.bankSlipUrl) {
      window.open(boletoData.bankSlipUrl, '_blank');
    }
  };

  const handleClose = () => {
    closePaymentModal();
    // Recarregar status apos fechar (pode ter pago)
    setTimeout(() => {
      loadPaymentStatus();
    }, 1000);
  };

  const valorTotal = selectedCobranca?.faturaValor || selectedCobranca?.valorTotal || 0;

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            Pagamento
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {/* Valor */}
          <Box sx={{ p: 3, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Valor a pagar
            </Typography>
            <Typography variant="h4" fontWeight="bold" color="primary">
              R$ {valorTotal.toFixed(2).replace('.', ',')}
            </Typography>
            {selectedCobranca?.faturaCompetencia && (
              <Typography variant="caption" color="text.secondary">
                Competencia: {selectedCobranca.faturaCompetencia}
              </Typography>
            )}
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<PixIcon />}
              label="PIX"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<BoletoIcon />}
              label="Boleto"
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>

          {/* Conteudo */}
          <Box sx={{ p: 3 }}>
            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* PIX */}
            {!loading && !error && activeTab === 0 && pixData && (
              <Box textAlign="center">
                {/* QR Code */}
                {pixData.encodedImage && (
                  <Paper
                    elevation={2}
                    sx={{
                      display: 'inline-block',
                      p: 2,
                      mb: 3,
                      borderRadius: 2
                    }}
                  >
                    <img
                      src={`data:image/png;base64,${pixData.encodedImage}`}
                      alt="QR Code PIX"
                      style={{ width: 200, height: 200 }}
                    />
                  </Paper>
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Escaneie o QR Code com o app do seu banco ou copie o codigo abaixo:
                </Typography>

                {/* Codigo Copia e Cola */}
                <Paper
                  sx={{
                    p: 2,
                    mt: 2,
                    backgroundColor: '#f5f5f5',
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    maxHeight: 80,
                    overflow: 'auto'
                  }}
                >
                  {pixData.payload}
                </Paper>

                <Button
                  variant="contained"
                  startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  onClick={handleCopyPix}
                  fullWidth
                  sx={{ mt: 2 }}
                  color={copied ? 'success' : 'primary'}
                >
                  {copied ? 'Copiado!' : 'Copiar codigo PIX'}
                </Button>

                {pixData.expirationDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Valido ate: {new Date(pixData.expirationDate).toLocaleString('pt-BR')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Boleto */}
            {!loading && !error && activeTab === 1 && boletoData && (
              <Box textAlign="center">
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Linha digitavel:
                </Typography>

                {/* Linha Digitavel */}
                <Paper
                  sx={{
                    p: 2,
                    mt: 1,
                    mb: 3,
                    backgroundColor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    letterSpacing: 1
                  }}
                >
                  {boletoData.identificationField}
                </Paper>

                <Box display="flex" gap={2}>
                  <Tooltip title="Copiar linha digitavel">
                    <Button
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyBoleto}
                      fullWidth
                    >
                      Copiar codigo
                    </Button>
                  </Tooltip>

                  <Tooltip title="Abrir boleto em PDF">
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleOpenBoleto}
                      fullWidth
                      disabled={!boletoData.bankSlipUrl}
                    >
                      Abrir boleto
                    </Button>
                  </Tooltip>
                </Box>

                {boletoData.nossoNumero && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                    Nosso numero: {boletoData.nossoNumero}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Apos o pagamento, o acesso sera liberado automaticamente.
          </Typography>
          <Button onClick={handleClose} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de confirmacao */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Codigo copiado para a area de transferencia"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default PaymentModal;
